import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
    "https://images.pexels.com/photos/10386893/pexels-photo-10386893.jpeg?auto=compress&cs=tinysrgb&w=1920";

const TICKER = [
    { value: "API 6A", label: "Wellhead & Drilling Equipment" },
    { value: "API 6D", label: "Pipeline & Piping Valves" },
    { value: "15,000 PSI", label: "High-Pressure Service" },
    { value: '2" – 48"', label: "Bore Range" },
];

const lineReveal = (i) => ({
    initial: { y: "115%" },
    animate: { y: "0%" },
    transition: {
        duration: 1,
        delay: 0.45 + i * 0.16,
        ease: [0.16, 1, 0.3, 1],
    },
});

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
    const hudFade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            ref={ref}
            className="relative flex min-h-screen flex-col overflow-hidden"
        >
            {/* full-bleed oil & gas visual with slow parallax */}
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Illuminated oil refinery at night"
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-obsidian/60" />
                <div className="absolute inset-0 bg-gradient-to-r from-obsidian/90 via-obsidian/25 to-transparent" />
            </motion.div>

            {/* giant outlined wordmark */}
            <div
                aria-hidden
                className="pointer-events-none absolute -right-6 top-20 hidden select-none lg:block"
            >
                <span className="text-outline font-display text-[11rem] font-black uppercase leading-none tracking-tight">
                    Blackhawk
                </span>
            </div>

            {/* HUD corner labels */}
            <motion.div
                style={{ opacity: hudFade }}
                className="pointer-events-none absolute inset-x-0 top-24 z-10 hidden justify-between px-8 font-mono text-[10px] uppercase tracking-[0.35em] text-slate-400 md:flex"
            >
                <span>Blackhawk Valves Ltd</span>
                <span className="text-tactical">API 6A · API 6D</span>
            </motion.div>

            <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-end px-6 pb-12 pt-44 sm:px-8">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.25 }}
                    className="mb-6 flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-tactical"
                >
                    <span className="h-px w-10 bg-tactical" />
                    Oil &amp; Gas Flow Control — Manufactured in the UK
                </motion.p>

                <h1 className="font-display text-[19vw] font-black uppercase leading-[0.86] tracking-tight text-slate-100 sm:text-8xl lg:text-[8.5rem]">
                    {["Flow,", "Controlled."].map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1">
                            <motion.span className="block" {...lineReveal(i)}>
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.0 }}
                    className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                >
                    Blackhawk Valves engineers and manufactures API 6A and
                    API 6D valve systems for the world&apos;s most demanding
                    oil &amp; gas operations — from wellhead to pipeline,
                    built and hydrostatically tested in Britain.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.15 }}
                    className="mt-9 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-quote-request-btn"
                        onClick={() => scrollToId("contact")}
                        className="group relative overflow-hidden bg-tactical px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-transform duration-300 hover:-translate-y-0.5"
                    >
                        <span className="relative z-10">Request a Quote</span>
                        <span className="absolute inset-0 -translate-x-full bg-slate-100 transition-transform duration-500 group-hover:translate-x-0" />
                    </button>
                    <button
                        data-testid="hero-explore-products-btn"
                        onClick={() => scrollToId("products")}
                        className="border border-slate-500/60 px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-200 transition-colors duration-300 hover:border-tactical hover:text-tactical"
                    >
                        Explore the Range
                    </button>
                </motion.div>
            </div>

            {/* spec ticker */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1.35 }}
                className="relative z-10 border-t border-white/10 bg-obsidian/50 backdrop-blur-sm"
                data-testid="hero-spec-ticker"
            >
                <div className="mx-auto grid max-w-7xl grid-cols-2 md:grid-cols-4">
                    {TICKER.map((t, i) => (
                        <div
                            key={t.label}
                            className={`px-6 py-4 sm:px-8 ${i > 0 ? "border-l border-white/10" : ""} ${i > 1 ? "border-t border-white/10 md:border-t-0" : ""} ${i === 2 ? "border-l-0 md:border-l" : ""}`}
                        >
                            <p className="font-display text-xl font-bold uppercase text-slate-100">
                                {t.value}
                            </p>
                            <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">
                                {t.label}
                            </p>
                        </div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
}
